import React, { useEffect, useState } from "react";
import {
  DialogContent,
  Dialog,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import CartStepper from "../cartDialogComponents/stepper";
import UploadPrescription from "../cartDialogComponents/uploadPrescription";
import CartAdd from "../cartDialogComponents/cartAdd";
import CartSummary from "../cartDialogComponents/cartSummary";
import OrderVerification from "../cartDialogComponents/orderVerification";
import {
  fetchAmountSplit,
  requestPayment,
  uploadFilesAPI,
  validateOrderDetails,
} from "../apiCalls";
import { toast } from "react-toast";
import { ToastEmitter } from "../../DoctorMangement/ToastEmitter";
import CloseIcon from "@mui/icons-material/Close";
import { visionContractId } from "../constants";
import { EVENTS } from "../../centralOms/events_constant";
import { logEventToAnalyticsPlatform } from "../../../helper/events";

const CartDialog = ({
  cartDialogVisible,
  orderId,
  patientName,
  setCartDialogVisibility,
  setNavigateToAll,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [partnerOrderId, setPartnerOrderId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [walletAmount, setWalletAmount] = useState(0);
  const [reviewMessage, setReviewMessage] = useState("");

  const handleAddItemsNextAction = async () => {
    setIsLoading(true);
    try {
      const body = {
        orderId: orderId,
        customerName: patientName,
        items: cartItems,
      };
      const response = await fetchAmountSplit(body);
      if (response.success) {
        if(response.data.opsReviewRequired) {
          setReviewMessage(response.data.message);
          setWalletAmount(-1);
        } else {
          setWalletAmount(response.data?.paymentSummary?.totalWalletAmount || 0);
        }
        setActiveStep(activeStep + 1);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayment = async (otpId) => {
    try {
      const userDetails = JSON.parse(localStorage.getItem("user_details"));
      const body = {
        orderId: orderId,
        customerName: patientName,
        vendorOrderId: partnerOrderId,
        items: cartItems,
        prescriptionDetails: prescriptions,
        providerId: userDetails.providerId ? userDetails.providerId : userDetails.parentId,
        paymentSuccessWebhook: {
          type: "Email",
          value: userDetails.userEmail
        }
      };
      const response = await requestPayment(body, otpId);
      if (response.success) {
        logEventToAnalyticsPlatform({
          name: EVENTS.PC_LS_ACTION.name,
          payload: {
            tab_name: EVENTS.TAB_NAME_ACTION_PENDING,
            filterValue: 'true',
            reference_id: '' + orderId,
            reference_provider_id: '' + userDetails.providerId ? userDetails.providerId : userDetails.parentId,
            service_name: EVENTS.SERVICE_NAME_VISION,
            action: EVENTS.ACTION.INITIATE_PAYMENT_CLICKED,
          },
          schema: EVENTS.PC_LS_ACTION.schema,
        });
        setCartDialogVisibility(false);
        setNavigateToAll(true);
        ToastEmitter("Payment Requested Successfully", "success");
      }
    } catch (error) {
    }
  };

  const uploadFiles = async () => {
    setIsLoading(true);
    try {
      const guids = await uploadFilesAPI(uploadedFiles, visionContractId);
      if (guids.length > 0) {
        setPrescriptions(guids);
        setActiveStep(activeStep + 1);
      } else {
        toast.error("File upload failed");
      }
    } catch (err) {
      toast.error("File upload failed");
    } finally {
      setIsLoading(false);
    }
  };

  // Define your steps with labels and content
  const initialSteps = [
    {
      label: "Upload Prescription",
      content: (
        <UploadPrescription
          uploadedFiles={uploadedFiles}
          setUploadedFiles={setUploadedFiles}
        />
      ),
      disabled: uploadedFiles.length === 0,
      nextAction: uploadFiles,
    },
    {
      label: "Add Items",
      content: <CartAdd cartItems={cartItems} setCartItems={setCartItems} />,
      disabled: cartItems.length === 0,
      nextAction: handleAddItemsNextAction,
    },
    {
      label: "Order Summary",
      content: (
        <CartSummary cartItems={cartItems} walletAmount={walletAmount} reviewMessage={reviewMessage}/>
      ),
    },
    {
      label: "Order Verification",
      content: (
        <OrderVerification
          partnerOrderId={partnerOrderId}
          setPartnerOrderId={setPartnerOrderId}
          handlePayment={handlePayment}
          orderId={orderId}
          setIsLoading={setIsLoading}
        />
      ),
    },
  ];
  const [isRXStepRequired, setIsRXStepRequired] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await validateOrderDetails(orderId);
        if (response.success) {
          if (!response.data.rxGenerationRequired) {
            setIsRXStepRequired(false);
          }
        } else {
          setCartDialogVisibility(false);
        }
      } catch (error) {
        console.error("Error validating order details:", error);
        setCartDialogVisibility(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle step change
  const handleStepChange = (step) => {
    setActiveStep(step);
  };

  return (
    <Dialog
      open={cartDialogVisible}
      onClose={() => setCartDialogVisibility(false)}
      fullWidth={true}
      maxWidth={"md"}
    >
      <div className="flex justify-end">
        <div
          className="text-gray-500 p-3"
          onClick={() => setCartDialogVisibility(false)}
        >
          <span className="cursor-pointer">
            <CloseIcon></CloseIcon>
          </span>
        </div>
      </div>
      <DialogContent maxWidth="md">
        {isLoading && (
          <Backdrop sx={{ color: "#fff", zIndex: 99999 }} open={isLoading}>
            <CircularProgress />
          </Backdrop>
        )}
        <CartStepper
          orderId={orderId}
          patientName={patientName}
          steps={isRXStepRequired ? initialSteps : initialSteps.slice(1)}
          activeStep={activeStep}
          onStepChange={handleStepChange}
          showNavigation={true}
        />
      </DialogContent>
    </Dialog>
  );
};

export default CartDialog;
